/**
 * Audit Log Types and Service
 * Requirements: 19.1, 19.2, 19.3, 19.4, 19.6
 */

export type AuditAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'workflow_transition'
  | 'publish'
  | 'login'
  | 'logout'
  | 'token_refresh';

export type AuditResourceType = 'course' | 'lesson' | 'block' | 'user';

export interface AuditLogEntry {
  id: string;
  timestamp: string;
  userId: string;
  action: AuditAction;
  resourceType: AuditResourceType;
  resourceId: string;
  previousState?: unknown;
  newState?: unknown;
  metadata?: Record<string, unknown>;
}

/**
 * Input for creating an audit log entry
 */
export interface LogActionInput {
  userId: string;
  action: AuditAction;
  resourceType: AuditResourceType;
  resourceId: string;
  previousState?: unknown;
  newState?: unknown;
  metadata?: Record<string, unknown>;
}

/**
 * Query filters for retrieving audit log entries
 */
export interface AuditLogQueryFilters {
  startDate?: Date;
  endDate?: Date;
  userId?: string;
  action?: AuditAction;
  resourceType?: AuditResourceType;
  resourceId?: string;
}

/**
 * Generate a unique audit log entry ID
 */
export function generateAuditLogId(): string {
  return `audit_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * In-memory audit log store (immutable entries)
 * In production, this would be backed by a database
 * Requirements: 19.6 - Entries are immutable
 */
let auditLogStore: readonly AuditLogEntry[] = [];

/**
 * Create an audit log entry
 * Requirements: 19.1, 19.2, 19.3, 19.4
 * 
 * @param input - The audit log entry data
 * @returns The created audit log entry
 */
export function logAction(input: LogActionInput): AuditLogEntry {
  const entry: AuditLogEntry = {
    id: generateAuditLogId(),
    timestamp: new Date().toISOString(),
    userId: input.userId,
    action: input.action,
    resourceType: input.resourceType,
    resourceId: input.resourceId,
    previousState: input.previousState,
    newState: input.newState,
    metadata: input.metadata,
  };

  // Store immutably by creating a new array
  auditLogStore = Object.freeze([...auditLogStore, entry]);

  return entry;
}

/**
 * Get all audit log entries (for testing/admin purposes)
 * Returns a frozen copy to maintain immutability
 */
export function getAuditLog(): readonly AuditLogEntry[] {
  return auditLogStore;
}

/**
 * Query audit log entries with filters
 * Requirements: 19.5
 * 
 * @param filters - Optional filters to apply
 * @returns Filtered audit log entries
 */
export function queryAuditLog(filters?: AuditLogQueryFilters): readonly AuditLogEntry[] {
  if (!filters) {
    return auditLogStore;
  }

  return auditLogStore.filter((entry) => {
    // Filter by date range
    if (filters.startDate) {
      const entryDate = new Date(entry.timestamp);
      if (entryDate < filters.startDate) {
        return false;
      }
    }
    if (filters.endDate) {
      const entryDate = new Date(entry.timestamp);
      if (entryDate > filters.endDate) {
        return false;
      }
    }

    // Filter by userId
    if (filters.userId && entry.userId !== filters.userId) {
      return false;
    }

    // Filter by action
    if (filters.action && entry.action !== filters.action) {
      return false;
    }

    // Filter by resourceType
    if (filters.resourceType && entry.resourceType !== filters.resourceType) {
      return false;
    }

    // Filter by resourceId
    if (filters.resourceId && entry.resourceId !== filters.resourceId) {
      return false;
    }

    return true;
  });
}

/**
 * Clear the audit log (for testing purposes only)
 * In production, audit logs should never be cleared
 */
export function clearAuditLog(): void {
  auditLogStore = [];
}

/**
 * Get the count of audit log entries
 */
export function getAuditLogCount(): number {
  return auditLogStore.length;
}

/**
 * Check if an audit log entry exists for a specific action and resource
 */
export function hasAuditEntry(
  action: AuditAction,
  resourceType: AuditResourceType,
  resourceId: string
): boolean {
  return auditLogStore.some(
    (entry) =>
      entry.action === action &&
      entry.resourceType === resourceType &&
      entry.resourceId === resourceId
  );
}
